/**
 * Mapping from US state name (as it appears in the regions GeoJSON `name`
 * property) to the basename of the corresponding ZIP-code polygons file.
 *
 * Source: https://github.com/OpenDataDE/State-zip-code-GeoJSON
 * Files are CC-licensed and derived from US Census Bureau ZCTA data (2010
 * cartographic boundary files).
 *
 * Usage in IncidentMap config:
 *
 *   US: {
 *     geoJsonUrl: '/geojson/usa-states.geojson',
 *     regionNameProperty: 'name',
 *     postcodeProperty: 'ZCTA5CE10',
 *     postcodeGeoJsonUrl: (regionName) => {
 *       const file = USA_STATE_FILES[regionName];
 *       return file ? `/geojson/usa/${file}.geojson` : null;
 *     },
 *   },
 *
 * NOTE: The original files use `.min.json` extension. If you keep that, just
 * adjust the path template above. If you rename to `.geojson` on disk (helps
 * nginx serve the right MIME type), drop the `.min` from filenames too.
 *
 * Size guidance — per-state file sizes range from ~50KB (Wyoming, DC) to
 * ~6MB (Texas). Lazy-loaded per region by usePolygonLoader, so the page
 * stays light until the user actually drills into a state.
 */
export const USA_STATE_FILES: Record<string, string> = {
  Alabama: 'al_alabama_zip_codes_geo.min',
  Alaska: 'ak_alaska_zip_codes_geo.min',
  Arizona: 'az_arizona_zip_codes_geo.min',
  Arkansas: 'ar_arkansas_zip_codes_geo.min',
  California: 'ca_california_zip_codes_geo.min',
  Colorado: 'co_colorado_zip_codes_geo.min',
  Connecticut: 'ct_connecticut_zip_codes_geo.min',
  Delaware: 'de_delaware_zip_codes_geo.min',
  'District of Columbia': 'dc_district_of_columbia_zip_codes_geo.min',
  Florida: 'fl_florida_zip_codes_geo.min',
  Georgia: 'ga_georgia_zip_codes_geo.min',
  Hawaii: 'hi_hawaii_zip_codes_geo.min',
  Idaho: 'id_idaho_zip_codes_geo.min',
  Illinois: 'il_illinois_zip_codes_geo.min',
  Indiana: 'in_indiana_zip_codes_geo.min',
  Iowa: 'ia_iowa_zip_codes_geo.min',
  Kansas: 'ks_kansas_zip_codes_geo.min',
  Kentucky: 'ky_kentucky_zip_codes_geo.min',
  Louisiana: 'la_louisiana_zip_codes_geo.min',
  Maine: 'me_maine_zip_codes_geo.min',
  Maryland: 'md_maryland_zip_codes_geo.min',
  Massachusetts: 'ma_massachusetts_zip_codes_geo.min',
  Michigan: 'mi_michigan_zip_codes_geo.min',
  Minnesota: 'mn_minnesota_zip_codes_geo.min',
  Mississippi: 'ms_mississippi_zip_codes_geo.min',
  Missouri: 'mo_missouri_zip_codes_geo.min',
  Montana: 'mt_montana_zip_codes_geo.min',
  Nebraska: 'ne_nebraska_zip_codes_geo.min',
  Nevada: 'nv_nevada_zip_codes_geo.min',
  'New Hampshire': 'nh_new_hampshire_zip_codes_geo.min',
  'New Jersey': 'nj_new_jersey_zip_codes_geo.min',
  'New Mexico': 'nm_new_mexico_zip_codes_geo.min',
  'New York': 'ny_new_york_zip_codes_geo.min',
  'North Carolina': 'nc_north_carolina_zip_codes_geo.min',
  'North Dakota': 'nd_north_dakota_zip_codes_geo.min',
  Ohio: 'oh_ohio_zip_codes_geo.min',
  Oklahoma: 'ok_oklahoma_zip_codes_geo.min',
  Oregon: 'or_oregon_zip_codes_geo.min',
  Pennsylvania: 'pa_pennsylvania_zip_codes_geo.min',
  'Rhode Island': 'ri_rhode_island_zip_codes_geo.min',
  'South Carolina': 'sc_south_carolina_zip_codes_geo.min',
  'South Dakota': 'sd_south_dakota_zip_codes_geo.min',
  Tennessee: 'tn_tennessee_zip_codes_geo.min',
  Texas: 'tx_texas_zip_codes_geo.min',
  Utah: 'ut_utah_zip_codes_geo.min',
  Vermont: 'vt_vermont_zip_codes_geo.min',
  Virginia: 'va_virginia_zip_codes_geo.min',
  Washington: 'wa_washington_zip_codes_geo.min',
  'West Virginia': 'wv_west_virginia_zip_codes_geo.min',
  Wisconsin: 'wi_wisconsin_zip_codes_geo.min',
  Wyoming: 'wy_wyoming_zip_codes_geo.min',
};
